// public-frontend/src/pages/CheckYourInbox.tsx
/**
 * "We sent you a confirmation link" for the PUBLIC site.
 *
 * A deliberate near-duplicate of frontend/src/components/CheckYourInbox.tsx.
 * The two apps share no component package and do not even share a styling
 * approach — the product app writes inline style objects, this one writes
 * Tailwind classes — so importing across would drag one convention into the
 * other. What MUST stay in step is the behaviour, not the markup:
 *   - the resend endpoint and its 60s cooldown,
 *   - and the rule below about what may be shown.
 *
 * ⚠️ THE RESEND RESPONSE IS DELIBERATELY UNINFORMATIVE. The endpoint answers
 * identically for an unverified address, a confirmed one, an OAuth account and
 * an address that never existed, so it cannot be used to test who has an
 * account. Never render "that address isn't registered" here — this component
 * does not know, and the server will not say.
 *
 * The confirmation link itself points at the PRODUCT app (`/#verify-email`),
 * which is where the page that redeems the token lives.
 */

import React, { useEffect, useState } from 'react';
import { log } from '../utils/logger';

const COMPONENT = 'CheckYourInbox';
const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';

/** Matches RESEND_COOLDOWN_SECONDS in the backend's EmailVerificationService. */
const RESEND_COOLDOWN_SECONDS = 60;

interface CheckYourInboxProps {
  email: string;
  dark?: boolean;
  /** False when the initial send failed, so the copy leads with Resend. */
  emailSent?: boolean;
  onBackToLogin: () => void;
}

const CheckYourInbox: React.FC<CheckYourInboxProps> = ({
  email,
  dark = false,
  emailSent = true,
  onBackToLogin,
}) => {
  const [cooldown, setCooldown] = useState(emailSent ? RESEND_COOLDOWN_SECONDS : 0);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const handleResend = async () => {
    setSending(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
        signal: AbortSignal.timeout(10000),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not send the email. Please try again shortly.');
        return;
      }
      // Whatever the server actually did, this is all we may say.
      setNotice(data.message || "If that address needs confirming, we've sent a new link.");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      log.error(COMPONENT, 'resend-verification failed', { err: String(err) });
      setError('Could not reach the server. Please try again shortly.');
    } finally {
      setSending(false);
    }
  };

  const d = dark;
  const card = d
    ? 'bg-gray-900/95 border-gray-700/60 shadow-2xl shadow-black/50'
    : 'bg-white border-gray-200 shadow-2xl shadow-gray-300/40';
  const canResend = cooldown <= 0 && !sending;

  return (
    <div className={`rounded-2xl border p-8 w-full max-w-md mx-auto backdrop-blur-xl ${card}`}>
      <div className="text-center mb-6">
        <div className="text-4xl mb-3" aria-hidden="true">✉️</div>
        <h2 className={`text-2xl font-black mb-1.5 ${d ? 'text-white' : 'text-gray-900'}`}>
          {emailSent ? 'Confirm your email' : "We couldn't send your email"}
        </h2>
      </div>

      <p className={`text-sm leading-relaxed mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
        {emailSent ? (
          <>
            We sent a confirmation link to{' '}
            <strong className={d ? 'text-white' : 'text-gray-900'}>{email}</strong>. Open it to
            finish setting up your account — you'll need to confirm this address before you can
            sign in.
          </>
        ) : (
          <>
            Your account was created, but the confirmation email to{' '}
            <strong className={d ? 'text-white' : 'text-gray-900'}>{email}</strong> did not go
            out. Use Resend below — you'll need to confirm this address before you can sign in.
          </>
        )}
      </p>

      <p className={`text-xs leading-relaxed mb-6 ${d ? 'text-gray-500' : 'text-gray-500'}`}>
        The link expires in 24 hours. If it isn't there in a minute or two, check your spam folder.
      </p>

      {notice && (
        <div
          role="status"
          className={`text-sm rounded-xl px-4 py-3 mb-4 ${
            d ? 'bg-emerald-950/60 text-emerald-300' : 'bg-emerald-50 text-emerald-800'
          }`}
        >
          {notice}
        </div>
      )}
      {error && (
        <div
          role="alert"
          className={`text-sm rounded-xl px-4 py-3 mb-4 ${
            d ? 'bg-red-950/60 text-red-300' : 'bg-red-50 text-red-800'
          }`}
        >
          {error}
        </div>
      )}

      <button
        onClick={handleResend}
        disabled={!canResend}
        className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {sending ? 'Sending…' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend confirmation email'}
      </button>

      <button
        onClick={onBackToLogin}
        className={`w-full mt-3 text-sm font-semibold ${
          d ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
        }`}
      >
        Back to sign in
      </button>
    </div>
  );
};

export default CheckYourInbox;
